import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Chip } from '@/ui/Chip/Chip'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Icon } from '@/ui/Icon/Icon'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { resourcesFor } from '../../api/resources'
import { ROUTES } from '../../constants/routes'
import { resourcesCopy } from '../../copy/resources'
import type { TopicResource } from '../../types/api'
import { RefreshFailed } from '../RefreshFailed'
import { ResourceRow } from './ResourceRow'
import { isConnectionKind, reviewFlag } from './resourceFacets'
import styles from './TopicResources.module.css'

type Filter = 'all' | 'connections' | 'agent_created' | 'needs_review'

interface Props {
  topicId: string
}

const CHIPS: { key: Filter; label: string }[] = [
  { key: 'all', label: resourcesCopy.facets.all },
  { key: 'connections', label: resourcesCopy.facets.connections },
  { key: 'agent_created', label: resourcesCopy.facets.agentCreated },
  { key: 'needs_review', label: resourcesCopy.facets.needsReview },
]

function matchesFilter(r: TopicResource, filter: Filter): boolean {
  if (filter === 'all') return true
  if (filter === 'needs_review') return reviewFlag(r) !== null
  if (filter === 'connections') return isConnectionKind(r.kind)
  return r.author?.kind === 'agent'
}

/**
 * The topic's Resources tab (§3): filter chips over kind/provenance
 * facets, then the rows in two groups, this topic's own and those shared
 * from the workspace, of [`ResourceRow`] — the shared row also used by the
 * thread panel and workspace index. The topic's own group ends with its
 * add row (D213: a list creates from its end).
 */
export function TopicResources({ topicId }: Props) {
  const [filter, setFilter] = useState<Filter>('all')
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['resources', 'topic', topicId],
    queryFn: () => resourcesFor({ kind: 'topic', id: topicId }).list(),
    staleTime: 10_000,
  })
  const showSkeleton = useSkeletonDelay(isLoading)
  const resources = useMemo(() => data?.resources ?? [], [data])

  const visible = resources.filter((r) => matchesFilter(r, filter))
  const groups: { key: string; label: string; items: TopicResource[] }[] = [
    {
      key: 'topic',
      label: resourcesCopy.groups.topic,
      items: visible.filter((r) => !r.workspace_id),
    },
    {
      key: 'workspace',
      label: resourcesCopy.groups.workspace,
      items: visible.filter((r) => !!r.workspace_id),
    },
  ]

  const addResource = (
    <Button
      variant="ghost"
      href={ROUTES.topicResourceNew(topicId)}
      state={{ returnTo: `${ROUTES.topic(topicId)}?tab=resources` }}
    >
      <Icon name="plus" size="icon-embedded" />
      {resourcesCopy.add}
    </Button>
  )

  let body: React.ReactNode
  if (isLoading) {
    body = showSkeleton && <Skeleton lines={3} />
  } else if (isError && !data) {
    body = (
      <Alert tone="danger">
        <Inline gap="space-8" align="baseline">
          {resourcesCopy.loadFailed}
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            {resourcesCopy.retry}
          </Button>
        </Inline>
      </Alert>
    )
  } else if (resources.length === 0) {
    body = (
      <EmptyState
        title={resourcesCopy.list.topicEmpty}
        titleAs="h2"
        action={addResource}
      />
    )
  } else if (visible.length === 0) {
    body = (
      <EmptyState
        variant="inline"
        description={resourcesCopy.list.filteredEmpty(filter)}
        action={
          <Button variant="outline" onClick={() => setFilter('all')}>
            {resourcesCopy.clearFilters}
          </Button>
        }
      />
    )
  } else {
    body = (
      <Stack gap="space-24">
        {groups.map(
          (g) =>
            (g.key === 'topic' || g.items.length > 0) && (
              <section key={g.key}>
                <SectionHeader
                  rule
                  trailingSpacing="section"
                  count={g.items.length}
                >
                  {g.label}
                </SectionHeader>
                <RowList variant="plain">
                  {g.items.map((r) => (
                    <ResourceRow key={r.id} resource={r} />
                  ))}
                  {g.key === 'topic' && (
                    <RowListItem>{addResource}</RowListItem>
                  )}
                </RowList>
              </section>
            ),
        )}
      </Stack>
    )
  }

  return (
    <Stack gap="space-24" className={styles.board}>
      <Inline role="group" aria-label={resourcesCopy.filterGroup} gap="space-6">
        {CHIPS.map((c) => (
          <Chip
            key={c.key}
            variant="filter"
            size="default"
            label={c.label}
            count={
              isLoading
                ? undefined
                : resources.filter((r) => matchesFilter(r, c.key)).length
            }
            pressed={filter === c.key}
            onPressedChange={() => setFilter(c.key)}
          />
        ))}
      </Inline>
      <div aria-busy={isLoading || undefined}>
        {isError && data && (
          <RefreshFailed
            message={resourcesCopy.loadFailed}
            onRetry={() => void refetch()}
          />
        )}
        {body}
      </div>
    </Stack>
  )
}
